import { identityApi } from "@/api/IdentityApi";
import {
  ConfirmEmailRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  RegisterAgentRequest,
  RegisterUserRequest,
  RegisterResponse,
  ResetPasswordRequest,
  ResendEmailConfirmationRequest,
} from "@/types/IdentityTypes";
import { type ReactNode } from "react";
import { IdentityContext } from "./UseIdentity";
import { config } from "@/lib/config";
import { trackConversion } from "@/lib/analytics";

export interface AuthProviderProps {
  children: ReactNode;
  loadingElement?: ReactNode;
}

const API_BASE_URL = config.VITE_IDENTITY_API_URL;

export const IdentityProvider = ({ children }: AuthProviderProps) => {
  const externalLogin = (loginProvider: string, returnUrl: string) => {
    window.location.href = `${API_BASE_URL}/api/identity/external-login?scheme=${loginProvider}&returnUrl=${returnUrl}`;
  };

  const linkExternalProvider = (loginProvider: string, returnUrl: string) => {
    window.location.href = `${API_BASE_URL}/api/identity/link-external-provider?scheme=${loginProvider}&returnUrl=${returnUrl}`;
  };

  const logout = async (
    logoutId?: string | undefined,
  ): Promise<LogoutResponse> => {
    return await identityApi.logout(logoutId);
  };

  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    const loginResponse = await identityApi.login(credentials);
    return loginResponse;
  };

  const registerAgent = async (
    request: RegisterAgentRequest,
  ): Promise<RegisterResponse> => {
    const response = await identityApi.registerAgent(request);
    trackConversion("sign_up");
    return response;
  };

  const registerUser = async (
    request: RegisterUserRequest,
  ): Promise<RegisterResponse> => {
    const response = await identityApi.registerUser(request);
    trackConversion("sign_up");
    return response;
  };

  const verifyEmailCode = async (data: ConfirmEmailRequest): Promise<void> => {
    await identityApi.confirmEmail(data);
  };

  const resendEmailConfirmation = async (
    data: ResendEmailConfirmationRequest,
  ): Promise<void> => {
    await identityApi.resendEmailConfirmation(data);
  };

  const requestPasswordReset = async (request: ForgotPasswordRequest) => {
    await identityApi.forgotPassword(request);
  };

  const resetPassword = async (request: ResetPasswordRequest) => {
    await identityApi.resetPassword(request);
  };

  return (
    <IdentityContext.Provider
      value={{
        externalLogin,
        linkExternalProvider,
        logout,
        login,
        registerAgent,
        registerUser,
        verifyEmailCode,
        resendEmailConfirmation,
        requestPasswordReset,
        resetPassword,
      }}
    >
      {children}
    </IdentityContext.Provider>
  );
};
